import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export type TradingPlatform = 'MT4' | 'MT5';

export interface Account {
  id: string;
  user_id: string;
  account_login: string;
  platform: TradingPlatform;
  broker_server: string;
  nickname: string | null;
  connection_status: 'pending' | 'connected' | 'disconnected' | 'error';
  equity_start_of_day: number | null;
  current_equity: number | null;
  current_balance: number | null;
  current_margin: number | null;
  current_free_margin: number | null;
  daily_dd: number | null;
  margin_level: number | null;
  kill_switch_stage: number;
  locked_until: string | null;
  timezone: string;
  last_equity_reset: string | null;
  last_sync_at: string | null;
  last_snapshot_at: string | null;
  last_command_poll_at: string | null;
  last_command_complete_at: string | null;
  created_at: string;
  updated_at: string;
}

export const useAccounts = () => {
  return useQuery({
    queryKey: ['accounts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accounts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as unknown as Account[];
    },
  });
};

export const useAccount = (id: string) => {
  return useQuery({
    queryKey: ['account', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accounts')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      return data as unknown as Account | null;
    },
    enabled: !!id,
  });
};

export const useActiveAccount = () => {
  return useQuery({
    queryKey: ['accounts', 'active'],
    queryFn: async () => {
      // 1. Read active_account_id from profile
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data: profile } = await supabase
        .from('profiles')
        .select('active_account_id')
        .eq('user_id', user.id)
        .maybeSingle();

      // 2. If profile has active_account_id, fetch that account
      if (profile?.active_account_id) {
        const { data, error } = await supabase
          .from('accounts')
          .select('*')
          .eq('id', profile.active_account_id)
          .maybeSingle();

        if (!error && data) return data as unknown as Account;
      }

      // 3. Fallback: most recently updated account
      const { data, error } = await supabase
        .from('accounts')
        .select('*')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as unknown as Account | null;
    },
  });
};

export const useCreateAccount = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (account: {
      account_login: string;
      platform: TradingPlatform;
      broker_server: string;
      timezone?: string;
      nickname?: string;
      account_type?: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const insertData = {
        user_id: user.id,
        account_login: account.account_login,
        platform: account.platform,
        broker_server: account.broker_server,
        timezone: account.timezone || 'UTC',
        ...(account.nickname ? { nickname: account.nickname } : {}),
        ...(account.account_type ? { account_type: account.account_type } : {}),
      };

      const { data, error } = await supabase
        .from('accounts')
        .insert([insertData as never])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: () => {
      // Error handling is done by the caller for better UX
    },
  });
};

export const useUpdateAccount = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Account> }) => {
      const { data, error } = await supabase
        .from('accounts')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

export const useDisconnectAccount = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ accountId, isActive }: { accountId: string; isActive: boolean }) => {
      // Soft disconnect: set status to disconnected
      const { error: updateError } = await supabase
        .from('accounts')
        .update({ connection_status: 'disconnected' } as never)
        .eq('id', accountId);

      if (updateError) throw updateError;

      // If this was the active account, nullify active_account_id
      if (isActive) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase
            .from('profiles')
            .update({ active_account_id: null } as never)
            .eq('user_id', user.id);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      toast({
        title: 'Disconnected',
        description: 'Account has been disconnected. Data is preserved.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteAccount = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('accounts')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      toast({
        title: 'Account removed',
        description: 'MetaTrader account has been permanently deleted.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};
